// src/components/documents/LineDescriptionInput.js — linetext-4d8b2e61 (Stage 2, web)
//
// THE field a line-item description is typed into, on every web editor (the estimate pop-up and page, the
// invoice, job and recurring-invoice pop-ups, job templates). It owns its whole look so all six change together
// (Marcus, 2026-10-29: the old grey strip under the name looked disabled / auto-filled):
//   - its own visible "Description" label above it, in the editor's own label style (labelClassName);
//   - a normal editable field: white, the normal field border, dark normal-weight text, the standard placeholder
//     grey, a clear focus ring — and a separate field under the name, not a strip attached to it;
//   - plain text: Enter starts a new line, leading spaces and "- " bullets are kept as typed;
//   - it grows with what is typed (up to MAX_PX, then scrolls) and stops at 4,000 characters (QuickBooks' limit).
// Every other prop (name, value, onChange, disabled, id…) passes straight through, so each editor's save code is
// untouched. src/utils/lineDescriptionGuard.test.js fails if an editor types a line description into anything else;
// src/utils/lineDescriptionEnter.test.js presses Enter in every editor.
import React, { useId, useLayoutEffect, useRef } from 'react';
import { LINE_TEXT_MAX } from '../../utils/lineText';
import { LINE_FIELD } from './fieldStyles';

const MAX_PX = 320;
export const LINE_DESCRIPTION_LABEL = 'Description';
export const LINE_DESCRIPTION_PLACEHOLDER = 'Add details — press Enter for a new line, start a line with - for a bullet';

// The pop-ups' editable look (their name field's border and blue focus ring); the document pages' look is their own
// line field (fieldStyles.js LINE_FIELD: purple ring). Both: white, dark normal text, the standard placeholder grey.
const LOOKS = {
    popup: 'w-full p-2 border rounded-lg bg-white text-sm font-normal text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
    page: `${LINE_FIELD} py-2 leading-snug font-normal placeholder:text-gray-400`,
};

// Fit the box to its text. Run on every change, and again whenever the box's WIDTH changes (the page's styles
// arriving after the first paint, a resized window, a phone turned sideways) — measured too early, a box keeps
// the wrong height until the next keystroke. A hidden box (width 0) is left alone until it shows.
// Only writes when the height or scrolling actually changes, so an unchanged box never moves the layout.
export function fit(el) {
    if (!el || !el.offsetWidth) return;
    const before = el.style.height;
    el.style.height = 'auto';
    const full = el.scrollHeight + (el.offsetHeight - el.clientHeight);
    const height = `${Math.min(full, MAX_PX)}px`;
    const overflowY = full > MAX_PX ? 'auto' : 'hidden';
    el.style.height = height === before ? before : height;
    if (el.style.overflowY !== overflowY) el.style.overflowY = overflowY;
}

// Re-fit when the box's WIDTH changes — never inside the ResizeObserver's own delivery (that is what raised
// "ResizeObserver loop completed with undelivered notifications" when the job pop-up opened, 2026-10-29): the
// measure waits for the next animation frame, at most one is queued, it is cancelled on unmount, and a change of
// HEIGHT alone (which our own fit causes) never schedules another one.
export function watchWidth(el, { RO = typeof ResizeObserver === 'undefined' ? null : ResizeObserver,
    raf = (f) => window.requestAnimationFrame(f), caf = (id) => window.cancelAnimationFrame(id) } = {}) {
    if (!el || !RO) return () => {};
    let width = null;
    let frame = null;
    const ro = new RO((entries) => {
        const entry = entries && entries[entries.length - 1];
        const w = Math.round(entry && entry.contentRect ? entry.contentRect.width : el.offsetWidth);
        if (w === width) return; // a height change (ours), or nothing — no re-measure
        width = w;
        if (frame !== null) caf(frame);
        frame = raf(() => { frame = null; fit(el); });
    });
    ro.observe(el);
    return () => { ro.disconnect(); if (frame !== null) caf(frame); frame = null; };
}

export default function LineDescriptionInput({ value, id, labelClassName, look = 'popup', ...rest }) {
    const ref = useRef(null);
    const autoId = useId();
    const fieldId = id || `line-desc-${autoId}`;
    useLayoutEffect(() => { fit(ref.current); }, [value]);
    useLayoutEffect(() => watchWidth(ref.current), []);
    return (
        <div className="mt-2">
            <label htmlFor={fieldId} className={labelClassName || 'text-[10px] font-bold text-gray-400 uppercase mb-1 block'}>{LINE_DESCRIPTION_LABEL}</label>
            <textarea
                ref={ref}
                rows={1}
                maxLength={LINE_TEXT_MAX}
                {...rest}
                id={fieldId}
                placeholder={LINE_DESCRIPTION_PLACEHOLDER}
                value={value ?? ''}
                className={`block resize-none ${LOOKS[look] || LOOKS.popup}`}
            />
        </div>
    );
}
